import { isCoreAiPath, CORE_AI_PATHS } from './coreAiPaths';
import {
  ACOUSTIC_ARTICULATIONS,
  ENGINE_TYPES,
  FILTER_TYPES,
  LFO_SHAPES,
  MACRO_NAMES,
  MODULATION_DESTINATIONS,
  OSCILLATOR_WAVEFORMS,
  PHYSICAL_MODELS,
  cloneInstrumentPatch,
  normalizeInstrumentPatch
} from './patchSchema';
import { ARP_RATE_VALUES } from './arpTiming';

const range = (min, max, unit = 'normalized') => ({ type: 'number', min, max, unit });
const choice = (values) => ({ type: 'enum', values });
const boolean = { type: 'boolean' };
const AI_ENGINE_TYPES = ENGINE_TYPES.filter((engine) => !['sample','granular'].includes(engine));

export const INSTRUMENT_AI_CAPABILITIES = Object.freeze({
  contract: 'cookup-native-instrument-soundspec',
  contractVersion: 2,
  patchVersion: 6,
  architecture: {
    layers: 4,
    engines: AI_ENGINE_TYPES,
    signalFlow: ['layers', 'filter', 'filter2?', 'amp-envelope', 'distortion', 'parallel-fx', 'compressor', 'limiter', 'track-bus'],
    polyphony: { min: 8, max: 32, guaranteedSimultaneousVoices: 2, oscillatorBudget: 256, behavior: 'Harmony/keys/pads stay polyphonic. Core basses/leads may deliberately use mono/legato with 1 voice; preserve the selected design.' },
    modulation: ['amp-envelope', 'bipolar-filter-envelope', 'lfo-1', 'lfo-2', 'step-modulator', 'velocity', 'key-track', 'macros'],
    effects: ['distortion', 'chorus', 'delay', 'reverb', 'compressor']
  },
  parameters: {
    macros: Object.fromEntries(MACRO_NAMES.map((name) => [name, range(0, 1)])),
    layer: {
      core: { editableScalarPaths: CORE_AI_PATHS, assetPolicy: 'Keep exact customTable references. Assign real presets to change factory source identity.' },
      enabled: boolean,
      engine: choice(AI_ENGINE_TYPES),
      waveform: choice(OSCILLATOR_WAVEFORMS),
      octave: range(-3, 3, 'octaves'), semitone: range(-12, 12, 'semitones'), detune: range(-100, 100, 'cents'),
      level: range(0, 1), pan: range(-1, 1), tone: range(0, 1), width: range(0, 1), unison: range(1, 8, 'voices'),
      ratio: range(0.25, 16), index: range(0, 16), feedback: range(0, 1),
      wavetable: { position: range(0,1), warp: range(0,1), warpMode: choice(['none','bend','mirror','sync','fold','crush']), scanRate: range(.01,20,'Hz'), scanDepth: range(0,1) },
      spectral: { stretch:range(.25,3),shift:range(-12,12),tilt:range(-1,1),oddEven:range(-1,1),smear:range(0,1),partials:range(4,32) },
      afm: { algorithm: range(0, 7), operators: { length: 6, parameters: { ratio: range(0.125,32), mode: choice(['ratio','fixed']), frequency: range(1,20000,'Hz'), detune: range(-100,100,'cents'), level: range(0,1), feedback: range(0,1), attack: range(0.001,8), decay: range(0.001,8), sustain: range(0,1), release: range(0.005,12), velocity: range(0,1), wave: range(0,2) } } },
      harmonics: { type: 'number-array', length: 16, min: 0, max: 1 }, harmonicTilt: range(0, 1),
      density: range(0, 1), size: range(0, 1), noiseColor: choice(['white', 'pink', 'brown']),
      inharmonicity: range(0, 1), damping: range(0, 1),
      physicalModel: choice(PHYSICAL_MODELS), exciter: range(0, 1), bodySize: range(0, 1),
      bodyDecay: range(0, 1), material: range(0, 1), pickPosition: range(0.02, 0.98), breath: range(0, 1),
      articulation: choice(ACOUSTIC_ARTICULATIONS), bodyMix: range(0, 1), sympathetic: range(0, 1),
      coupling: range(0, 1), mechanics: range(0, 1), variation: range(0, 1), micPosition: range(0, 1),
      sampleStart: range(0, 3600, 'seconds'), sampleEnd: range(0, 3600, 'seconds'), loopEnabled: boolean,
      loopStart: range(0, 3600, 'seconds'), loopEnd: range(0, 3600, 'seconds'), loopCrossfade: range(0, 2, 'seconds'),
      envelope: { enabled: boolean, attack: range(0.001, 8, 'seconds'), decay: range(0.001, 8, 'seconds'), sustain: range(0, 1), release: range(0.005, 12, 'seconds') }
    },
    envelope: { attack: range(0.001, 8, 'seconds'), decay: range(0.001, 8, 'seconds'), sustain: range(0, 1), release: range(0.005, 12, 'seconds') },
    filter: { enabled: boolean, type: choice(FILTER_TYPES), cutoff: range(20, 20000, 'hertz'), resonance: range(0.0001, 24) },
    lfo: { enabled: boolean, shape: choice(LFO_SHAPES), rate: range(0.02, 30, 'hertz-or-beats'), depth: range(0, 1), sync: boolean, destination: choice(MODULATION_DESTINATIONS) },
    arp: { enabled: boolean, mode: choice(['up', 'down', 'random']), rate: choice(ARP_RATE_VALUES), octaves: range(1, 4, 'octaves'), gate: range(0.05, 1) }
  },
  engineGuidance: {
    core: 'Primary expressive synth: Rust/WASM band-limited wavetable oscillators, dual filters, envelopes, modulation matrix and effects. Prefer a fitting CookUp Signature preset before changing scalar Core parameters. Preserve its original tables. Core ampEnv/filter/fx control this engine; outer filter/ampEnvelope do not. Bound Air/Motion/Body/Space macros control Core directly.',
    wavetable: 'AudioWorklet wavetable with up to 16 Fourier frames and 64 harmonics, continuous scanning, approximate bend/mirror/sync/fold/crush warp, and per-layer modulation. Not Serum-compatible DSP.',
    analog: 'Basic-wave subtractive synthesis with 1-8 voice unison; best for basses, leads, pads and supersaws.',
    afm: 'Six Rust/WASM phase-modulation operators. Eight CookUp algorithms; independent envelopes, ratio or fixed Hz, feedback and velocity. Not Yamaha SysEx emulation.',
    fm: 'Carrier/modulator synthesis with ratio, index and audible feedback; best for bells, electric keys, growls and metallic attacks.',
    additive: 'Sixteen directly controllable harmonic amplitudes; best for organs, formants and exact harmonic spectra.',
    texture: 'Pitch-tracking white, pink or brown noise; best for breath, air, attacks and noisy layers.',
    resonator: 'Sixteen decaying, optionally inharmonic sine resonances for synthetic modal tones.',
    physical: 'Instrument-specific physical modelling: piano hammer/string/soundboard, plucked string/body, bowed stick-slip friction, flute/reed/brass bore excitation, and inharmonic mallet/bell modes.'
  }
});

const FIXED_PATHS = new Set([
  'name', 'category', 'tags',
  ...MACRO_NAMES.map((name) => `macros.${name}`),
  ...['attack', 'decay', 'sustain', 'release', 'velocity'].map((key) => `ampEnvelope.${key}`),
  ...['attack', 'decay', 'sustain', 'release', 'amount'].map((key) => `filterEnvelope.${key}`),
  ...['enabled', 'type', 'cutoff', 'resonance', 'drive', 'keyTrack'].map((key) => `filter.${key}`),
  ...['enabled', 'type', 'cutoff', 'resonance'].map((key) => `filter2.${key}`),
  ...['enabled', 'shape', 'rate', 'depth', 'sync', 'destination'].flatMap((key) => [`lfo.${key}`, `lfo2.${key}`]),
  ...['enabled', 'rate', 'depth'].map((key) => `stepMod.${key}`),
  ...Array.from({ length: 8 }, (_, index) => `stepMod.steps.${index}`),
  ...['enabled', 'mode', 'rate', 'octaves', 'gate'].map((key) => `arp.${key}`),
  ...['master', 'glide', 'voices', 'pitchBendRange'].map((key) => `performance.${key}`),
  ...['enabled', 'drive', 'mix'].map((key) => `effects.distortion.${key}`),
  ...['enabled', 'rate', 'depth', 'mix'].map((key) => `effects.chorus.${key}`),
  ...['enabled', 'time', 'feedback', 'mix'].map((key) => `effects.delay.${key}`),
  ...['enabled', 'size', 'damping', 'mix'].map((key) => `effects.reverb.${key}`),
  ...['enabled', 'threshold', 'ratio', 'attack', 'release'].map((key) => `effects.compressor.${key}`)
]);

const LAYER_KEYS = new Set([
  'enabled', 'engine', 'name', 'waveform', 'octave', 'semitone', 'detune', 'level', 'pan', 'tone', 'width',
  'unison', 'ratio', 'index', 'feedback', 'harmonicTilt', 'density', 'size', 'noiseColor', 'inharmonicity', 'damping',
  'physicalModel', 'exciter', 'bodySize', 'bodyDecay', 'material', 'pickPosition', 'breath',
  'articulation', 'bodyMix', 'sympathetic', 'coupling', 'mechanics', 'variation', 'micPosition',
  'sampleStart', 'sampleEnd', 'loopEnabled', 'loopStart', 'loopEnd', 'loopCrossfade'
]);

const isAllowedPath = (path) => {
  if (FIXED_PATHS.has(path) || isCoreAiPath(path)) return true;
  if (/^layers\.[0-3]\.wavetable\.(?:position|warp|warpMode|scanRate|scanDepth)$/.test(path)) return true;
  if (/^layers\.[0-3]\.spectral\.(?:stretch|shift|tilt|oddEven|smear|partials)$/.test(path)) return true;
  if (/^layers\.[0-3]\.afm\.algorithm$/.test(path)) return true;
  if (/^layers\.[0-3]\.afm\.operators\.[0-5]\.(?:ratio|mode|frequency|detune|level|feedback|attack|decay|sustain|release|velocity|wave)$/.test(path)) return true;
  const layerMatch = /^layers\.([0-3])\.([A-Za-z]+)$/.exec(path);
  if (layerMatch) return LAYER_KEYS.has(layerMatch[2]);
  if (/^layers\.[0-3]\.envelope\.(?:enabled|attack|decay|sustain|release)$/.test(path)) return true;
  return /^layers\.[0-3]\.harmonics\.(?:[0-9]|1[0-5])$/.test(path);
};

const safePath = (path) => {
  const value = String(path || '');
  return !value.split('.').some((key) => ['__proto__', 'prototype', 'constructor'].includes(key)) && isAllowedPath(value);
};

const readPath = (object, path) => path.split('.').reduce((value, key) => value?.[key], object);

const writePath = (object, path, value) => {
  const keys = path.split('.');
  let cursor = object;
  keys.slice(0, -1).forEach((key,index) => { if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = /^\d+$/.test(keys[index+1]) ? [] : {}; cursor = cursor[key]; });
  cursor[keys[keys.length - 1]] = value;
};

export const applyAiPatchCommands = (currentPatch, commands = []) => {
  const working = cloneInstrumentPatch(currentPatch);
  const applied = [];
  const rejected = [];
  const input = Array.isArray(commands) ? commands.slice(0, 128) : [];

  input.forEach((command, index) => {
    const operation = command?.op;
    const path = String(command?.path || '');
    if (!['set', 'adjust', 'scale', 'toggle'].includes(operation) || !safePath(path)) {
      rejected.push({ index, reason: 'unsupported-operation-or-path', command });
      return;
    }
    const previous = readPath(working, path);
    let value = command.value;
    if (operation === 'adjust' || operation === 'scale') {
      if (!Number.isFinite(Number(previous)) || !Number.isFinite(Number(value))) {
        rejected.push({ index, reason: 'numeric-operation-requires-numbers', command });
        return;
      }
      value = operation === 'adjust' ? Number(previous) + Number(value) : Number(previous) * Number(value);
    } else if (operation === 'toggle') {
      if (typeof previous !== 'boolean') {
        rejected.push({ index, reason: 'toggle-requires-boolean', command });
        return;
      }
      value = !previous;
    }
    writePath(working, path, value);
    applied.push({ index, op: operation, path });
  });

  return { patch: normalizeInstrumentPatch(working), applied, rejected };
};

export const auditInstrumentPatch = (candidate) => {
  const patch = normalizeInstrumentPatch(candidate);
  const warnings = [];
  const audibleLayers = patch.layers.filter((layer) => layer.enabled && layer.level > 0.0001);
  if (audibleLayers.length === 0) warnings.push('Patch has no audible layers.');
  if (audibleLayers.some((layer) => layer.engine === 'sample' && layer.sampleZones.length === 0)) {
    warnings.push('A sample layer has no loaded zones.');
  }
  if (patch.performance.master <= 0.0001) warnings.push('Master level is silent.');
  if (patch.filter.enabled && patch.filter.type === 'lowpass' && patch.filter.cutoff < 30) warnings.push('Primary low-pass filter is effectively closed.');
  if (patch.filter2.enabled && patch.filter2.type === 'highpass' && patch.filter2.cutoff > 19000) warnings.push('Secondary high-pass filter removes almost the entire audible range.');
  const blockingIssueCount = warnings.length;
  if (patch.effects.delay.enabled && patch.effects.delay.feedback > 0.86) warnings.push('Delay feedback is near self-oscillation; the safety limiter will constrain it.');
  const oscillatorsPerVoice = patch.layers
    .filter((layer) => layer.enabled && layer.level > 0.0001)
    .reduce((total, layer) => {
      if (layer.engine === 'analog' || layer.engine === 'wavetable') return total + layer.unison;
      if (layer.engine === 'spectral') return total + layer.spectral.partials;
      if (layer.engine === 'granular') return total + 16;
      if (layer.engine === 'afm') return total + 6;
      if (layer.engine === 'fm') return total + 2;
      if (layer.engine === 'additive' || layer.engine === 'resonator') return total + Math.max(1, layer.harmonics.filter((value) => value > 0.0001).length);
      if (layer.engine === 'physical') return total + 9;
      return total + 1;
    }, 0);
  const safeVoices = Math.max(2, Math.floor(256 / Math.max(1, oscillatorsPerVoice)));
  if (patch.performance.voices > safeVoices) {
    warnings.push(`Polyphony will be safely limited to ${safeVoices} voices for this patch's oscillator complexity.`);
  }
  return { patch, playable: blockingIssueCount === 0, warnings };
};

export const createAiSoundSpec = (candidate = {}) => ({
  contract: INSTRUMENT_AI_CAPABILITIES.contract,
  contractVersion: INSTRUMENT_AI_CAPABILITIES.contractVersion,
  capabilities: INSTRUMENT_AI_CAPABILITIES,
  patch: normalizeInstrumentPatch(candidate)
});
